/**
 * `@richrich/i18n/react` — React binding (subpath entry).
 *
 * A plain React context over the core translator. Deliberately DOM-free and
 * side-effect-free (no `document`, `localStorage`, `navigator`) so React Native
 * can consume it unchanged — persistence and `<html lang>` syncing are the host
 * app's job, wired through `onLocaleChange`.
 *
 * CONTRACT:
 *   • `<I18nProvider locale onLocaleChange?>` is CONTROLLED — the host owns the
 *     `locale` value and re-renders with a new one when it changes.
 *   • `useI18n()` → `{ t, locale, setLocale }`. `setLocale(next)` does not mutate
 *     any local state; it calls `onLocaleChange(next)` and the host drives the
 *     re-render. Calling it without an `onLocaleChange` handler is a no-op.
 */
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { createTranslator, type Locale, type MessageKey, type TranslateParams } from "./index";

/** The value exposed by {@link useI18n}. */
export interface I18nContextValue {
  /** Translate a key for the active locale with `{name}` interpolation. */
  t: (key: MessageKey, params?: TranslateParams) => string;
  /** The active locale. */
  locale: Locale;
  /** Request a locale change; delegates to the provider's `onLocaleChange`. */
  setLocale: (next: Locale) => void;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export interface I18nProviderProps {
  /** The controlled active locale. */
  locale: Locale;
  /** Called when a consumer requests a locale change (host persists + re-renders). */
  onLocaleChange?: (next: Locale) => void;
  children: ReactNode;
}

/** Provide the active locale + translator to the tree. */
export function I18nProvider({ locale, onLocaleChange, children }: I18nProviderProps) {
  const value = useMemo<I18nContextValue>(() => {
    const translator = createTranslator(locale);
    return {
      t: translator.t,
      locale,
      setLocale: (next: Locale) => {
        onLocaleChange?.(next);
      },
    };
  }, [locale, onLocaleChange]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Read the active translator + locale controls. Throws outside an `I18nProvider`. */
export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (ctx === null) {
    throw new Error("useI18n must be used within an <I18nProvider>.");
  }
  return ctx;
}
